// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { useContactUsBranches, warehouseOptionLabel } from "@conklin/App/Widgets/ContactUs/contactUsBranches";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    pickupBranch: state.components.contactUsForm.fieldValues["pickupBranch"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PickupBranchStyles {
    pickupBranchSelect?: SelectPresentationProps;
}

export const pickupBranchStyles: PickupBranchStyles = {};

const styles = pickupBranchStyles;

const PickupBranch: React.FC<Props> = ({ fields, pickupBranch, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [pickupBranchErrorMessage, setPickupBranchErrorMessage] = React.useState("");
    const warehouses = useContactUsBranches(true);

    React.useEffect(() => {
        contactFormContext.validators.pickupBranch = validatePickupBranch;
        if (typeof pickupBranch !== "undefined") {
            validatePickupBranch();
        }
    }, [pickupBranch]);

    React.useEffect(() => {
        contactFormContext.validators.pickupBranch = validatePickupBranch;
    }, [isRequired]);

    const validatePickupBranch = () => {
        const errorMessage = isRequired && !pickupBranch ? (siteMessage("Field_Required", label) as string) : "";
        setPickupBranchErrorMessage(errorMessage);
        return !errorMessage;
    };

    const pickupBranchChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setFieldValue({ key: "pickupBranch", value: event.target.value });
    };

    return (
        <Select
            {...(styles.pickupBranchSelect as SelectProps)}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={pickupBranch || ""}
            error={pickupBranchErrorMessage}
            onChange={pickupBranchChangeHandler}
        >
            <option value="">{hintText || translate("Select a pick-up branch")}</option>
            {warehouses.map(warehouse => (
                <option key={warehouse.id} value={warehouse.name}>
                    {warehouseOptionLabel(warehouse)}
                </option>
            ))}
        </Select>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PickupBranch),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Pick-Up Branch",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Select a pick-up branch",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
